import React from 'react';
import { Trash2, Plus, Minus } from 'lucide-react';

interface Props {
  cart: any;
  onCheckout: () => void;
  onHold: () => void;
}

const POSCartPanel: React.FC<Props> = ({ cart, onCheckout, onHold }) => {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-gray-700">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold text-white">Cart ({cart.totalItems})</h2>
          {cart.items.length > 0 && (
            <button
              onClick={cart.clearCart}
              className="text-xs text-red-400 hover:text-red-300"
            >
              Clear All
            </button>
          )}
        </div>
        {/* Customer info */}
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            placeholder="Customer name"
            value={cart.customerName}
            onChange={(e) => cart.setCustomerName(e.target.value)}
            className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Phone"
            value={cart.customerPhone}
            onChange={(e) => cart.setCustomerPhone(e.target.value)}
            className="w-28 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {cart.items.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            No items in cart
          </div>
        ) : (
          cart.items.map((item: any) => (
            <div key={item.key} className="bg-gray-700 rounded-lg p-2">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white truncate">{item.productName}</p>
                  {(item.color || item.sizeName) && (
                    <p className="text-xs text-gray-400">
                      {item.color && <span>{item.color}</span>}
                      {item.color && item.sizeName && <span> / </span>}
                      {item.sizeName && <span>{item.sizeName}</span>}
                    </p>
                  )}
                  <p className="text-xs text-gray-400">{Number(item.unitPrice).toLocaleString()} MMK</p>
                </div>
                <button
                  onClick={() => cart.removeItem(item.key)}
                  className="text-red-400 hover:text-red-300 p-1"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="flex items-center justify-between mt-1">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => cart.updateQuantity(item.key, item.quantity - 1)}
                    className="w-6 h-6 flex items-center justify-center bg-gray-600 rounded text-white hover:bg-gray-500"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="w-8 text-center text-sm text-white font-medium">{item.quantity}</span>
                  <button
                    onClick={() => cart.updateQuantity(item.key, item.quantity + 1)}
                    disabled={item.quantity >= item.maxStock}
                    className="w-6 h-6 flex items-center justify-center bg-gray-600 rounded text-white hover:bg-gray-500 disabled:opacity-50"
                  >
                    <Plus size={12} />
                  </button>
                </div>
                <p className="text-sm font-bold text-white">
                  {((item.unitPrice * item.quantity) - item.discount).toLocaleString()} MMK
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Totals & Actions */}
      <div className="p-3 border-t border-gray-700 bg-gray-800 space-y-2">
        {/* Order Discount */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Subtotal:</span>
          <span className="text-white">{cart.subtotal.toLocaleString()} MMK</span>
        </div>
        {cart.itemDiscountTotal > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Item Discounts:</span>
            <span className="text-red-400">-{cart.itemDiscountTotal.toLocaleString()} MMK</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 shrink-0">Order Discount:</span>
          <input
            type="number"
            min="0"
            value={cart.orderDiscount || ''}
            onChange={(e) => cart.setOrderDiscount(Number(e.target.value) || 0)}
            placeholder="0"
            className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white text-right focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <span className="text-xs text-gray-400">MMK</span>
        </div>

        <div className="flex items-center justify-between text-lg font-bold pt-1 border-t border-gray-600">
          <span className="text-white">Total:</span>
          <span className="text-green-400">{cart.totalAmount.toLocaleString()} MMK</span>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-1">
          <button
            onClick={onHold}
            disabled={cart.items.length === 0}
            className="flex-1 py-2.5 bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 disabled:text-gray-400 text-white font-medium rounded-lg text-sm transition"
          >
            Hold
          </button>
          <button
            onClick={onCheckout}
            disabled={cart.items.length === 0}
            className="flex-[2] py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:text-gray-400 text-white font-bold rounded-lg text-sm transition"
          >
            Pay ({cart.totalAmount.toLocaleString()} MMK)
          </button>
        </div>

        {/* Notes */}
        <input
          type="text"
          placeholder="Add notes..."
          value={cart.notes}
          onChange={(e) => cart.setNotes(e.target.value)}
          className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
    </div>
  );
};

export default POSCartPanel;
